import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useTable, useSortBy, usePagination, useRowSelect } from "react-table";
import "tailwindcss/tailwind.css";
import { Empty, Input, Modal, Button, Form } from "antd";
import "../../styles.css";
import Vectorsorting from "../../assets/Vectorsorting.svg";
import ShimmerLoader from "../../loading";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import CustomPagination from "../../components/CustomWidgets/CustomPagination";
import arrowright from "../../assets/arrowright.svg";
import { useSelector } from "react-redux";
import Edit from "../../assets/Edit.svg";
import deleteicon from "../../assets/Order/Delete.svg";

const ProductGroupTable = ({
    additionalData,
    pagination,
    onRowClick,
    onDelete,
    onEditLine,
    showEdit,
    isSubgroup
}) => {
    const { t, i18n } = useTranslation();
    const [flag, setFlag] = useState(false);
    const navigate = useNavigate();
    const allStores = useSelector(
        (state) => state?.storeReducer?.selectedStore?.is_select_all_store
    );

    // ✅ Modal editing states
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [currentRow, setCurrentRow] = useState(null);
    const [form] = Form.useForm();

    const data = useMemo(() => additionalData || [], [additionalData]);

    useEffect(() => {
        if (additionalData?.length === 0 && !flag) {
            const timer = setTimeout(() => {
                setFlag(true);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [additionalData, flag]);

    const handleEditClick = useCallback((row) => {
        setCurrentRow(row);
        form.setFieldsValue({
            name: row.name ?? row.original?.name ?? "",
            name_ar: row.name_ar ?? row.original?.name_ar ?? "",
        });
        setIsModalVisible(true);
    }, [form]);

    const handleSave = useCallback(async () => {
        try {
            const values = await form.validateFields();
            const updated = {
                ...currentRow,
                ...values,
            };
            onEditLine?.(updated);
            setIsModalVisible(false);
            setCurrentRow(null);
        } catch (error) {
            console.error("Validation failed:", error);
        }
    }, [form, currentRow, onEditLine]);

    const handleCancel = useCallback(() => {
        setIsModalVisible(false);
        setCurrentRow(null);
        form.resetFields();
    }, [form]);

const columns = useMemo(() => {
    const baseColumns = [
        ...(showEdit
            ? [{
                Header: t("Group Id"),
                accessor: "id",
                width: "8%",
                Cell: ({ row }) => (
                    <div className="text-start">{row.original?.id}</div>
                ),
            }]
            : []),

        {
            Header: t("Group Name (English)"),
            accessor: "name",
            width: isSubgroup ? "15%" : "20%",
            Cell: ({ row }) => (
                <div className="text-start">{row.original?.name}</div>
            ),
        },
        {
            Header: t("Group Name (Arabic)"),
            accessor: "name_ar",
            width: isSubgroup ? "15%" : "20%",
            Cell: ({ row }) => (
                <div className="text-start">{row.original?.name_ar}</div>
            ),
        },

        // Show only for subgroups
       ...(isSubgroup
  ? [
      {
        Header:
         t("Parent Group"),
        accessor:
          i18n.language === "ar"
            ? "parent_name_ar"
            : "parent_name",
        width: "18%",
        Cell: ({ row }) => (
          <div className="text-start">
            {i18n.language === "ar"
              ? row.original?.parent_name_ar
              : row.original?.parent_name}
          </div>
        ),
      },
    ]
  : []),

        {
            Header: t("No. of Products"),
            accessor: "no_of_products",
            width: "10%",
            Cell: ({ row }) => (
                <div className="text-start">
                    {row.original?.no_of_products}
                </div>
            ),
        },
    ];

    const actionColumn = showEdit
        ? [
              {
                  Header: t("Actions"),
                  disableSortBy: true,
                  accessor: "actions",
                  width: "12%",
                  Cell: ({ row }) => (
                      <div className="flex gap-2 justify-start">
                          <img
                              src={Edit}
                              className="cursor-pointer h-4 w-4"
                              alt="Edit"
                              onClick={(e) => {
                                  e.stopPropagation();
                                  onEditLine(row.original);
                              }}
                          />
                          <img
                              src={deleteicon}
                              className="cursor-pointer h-4 w-4 mx-2"
                              alt="Delete"
                              onClick={(e) => {
                                  e.stopPropagation();
                                  onDelete(row.original);
                              }}
                          />
                      </div>
                  ),
              },
          ]
        : [];

    const arrowColumn = [
        {
            id: "arrow",
            disableSortBy: true,
            Header: "",
            width: "4%",
            Cell: () => (
                <button className="text-[#1b4497] rounded-lg">
                    <img
                        src={arrowright}
                        alt="arrowright"
                        className={`${i18n.language === "en" ? "" : "rotate-180"}`}
                        width={20}
                    />
                </button>
            ),
        },
    ];

    return [...baseColumns, ...actionColumn, ...arrowColumn];
}, [
    t,
    i18n.language,
    showEdit,
    isSubgroup,
    onEditLine,
    onDelete,
]);


    const {
        headerGroups,
        prepareRow,
        page,
        nextPage,
        setPageSize,
        state: { pageSize },
    } = useTable(
        {
            columns,
            data: data,
            initialState: { pageSize: 10 },
        },
        useSortBy,
        usePagination,
        useRowSelect
    );

    const [pageIndex, setpageIndex] = useState(1);

    useEffect(() => {
        const pageDetails = {
            limit: pageSize,
            offset: pageSize * (pageIndex - 1) < 0 ? 0 : pageSize * (pageIndex - 1),
        };

        if (pageSize) pagination(pageDetails);
    }, [pageSize, pageIndex]);


    return (
        <div
            className="container flex flex-col mx-auto mt-4"
            style={{ maxWidth: "100%" }}
        >
            {additionalData?.length === 0 && !flag ? (
                <div className="h-screen w-[90%] mt-2 ">
                    <ShimmerLoader />
                </div>
            ) : (
                <>
                    {/* Header */}
                    <table
                        className="font-normal max-w-[100%] sm:w-[97.6%] md:w-[97.6%] lg:w-[97.6%] xl:w-[97.6%] overflow-hidden rounded-lg"
                        style={{ tableLayout: "fixed" }}
                    >
                        <thead>
                            {headerGroups.map((headerGroup) => (
                                <tr
                                    key={headerGroup.id}
                                    {...headerGroup.getHeaderGroupProps()}
                                    className="bg-[#78CFEC66] text-sm font-semibold overflow-hidden whitespace-nowrap"
                                >
                                    {headerGroup.headers.map((column) => (
                                        <th
                                            key={column.id}
                                            {...column.getHeaderProps(column.getSortByToggleProps())}
                                            className="py-3 whitespace-nowrap"
                                            style={{ width: column.width }}
                                        >
                                            <div className="flex flex-row items-center ms-2 text-sm leading-tight ">
                                                                           {column.render("Header")}
                                                                           {column.canSort && (
                                                                             <span className="ms-1">
                                                                               {column.isSorted ? (
                                                                                 column.isSortedDesc ? (
                                                                                   <img
                                                                                     src={Vectorsorting}
                                                                                     style={{
                                                                                       minHeight: 10,
                                                                                       minWidth: 10,
                                                                                     }}
                                                                                     alt="vector"
                                                                                   />
                                                                                 ) : (
                                                                                   <img
                                                                                     className="rotate-180"
                                                                                     src={Vectorsorting}
                                                                                     alt="vector"
                                                                                   />
                                                                                 )
                                                                               ) : (
                                                                                 // 👇 Show a neutral icon when column is not sorted
                                                                                 <img
                                                                                   src={Vectorsorting}
                                                                                   alt="sortable"
                                                                                   style={{
                                                                                     minHeight: 10,
                                                                                     minWidth: 10,
                                                                                     opacity: 0.3,
                                                                                   }}
                                                                                 />
                                                                               )}
                                                                             </span>
                                                                           )}
                                                                         </div>
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                    </table>

                    {/* Body */}
                    <div
                        className="scrollbar-none bg-[#F7F9FD]"
                        style={{ overflowY: "auto", maxHeight: "600px" }}
                    >
                        <table
                            className="font-normal max-w-[100%] sm:w-[97.6%] md:w-[97.6%] lg:w-[97.6%] xl:w-[97.6%] overflow-hidden rounded-lg"
                            style={{ tableLayout: "fixed" }}
                        >
                            <tbody className="bg-[#F7F9FD] text-center overflow-hidden">
                                {page.map((row) => {
                                    prepareRow(row);
                                    return (
                                        <tr
                                            key={row.id}
                                            {...row.getRowProps()}
                                            className="relative hover:bg-white cursor-pointer transition duration-300 border-b border-gray-300 fixed-row-height"
                                            onClick={() =>
                                                onRowClick && onRowClick(row.original)
                                            }
                                        >
                                            {row.cells.map((cell, index) => (
                                                <td
                                                    key={index}
                                                    {...cell.getCellProps()}
                                                    className={`p-2 text-[#272728] h-1212 text-sm font-normal ${index < row.cells.length - 1 ? "relative" : ""
                                                        }`}
                                                    style={{ width: cell.column.width }}
                                                >
                                                    {cell.render("Cell")}
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })}
                                {page.length === 0 && (
                                    <tr>
                                        <td colSpan={"100%"}>
                                            <Empty
                                                className="mt-10"
                                                description={t("No data available")}
                                            />
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="">
                        <CustomPagination
                            data={data?.length}
                            pageIndex={pageIndex}
                            pageCount={page?.length}
                            canPreviousPage={pageIndex != 1 ? true : false}
                            canNextPage={data?.length == pageSize ? true : false}
                            gotoPage={(page) => {
                                nextPage(page);
                            }}
                            previousPage={() => {
                                setpageIndex(pageIndex - 1);
                            }}
                            nextPage={() => {
                                setpageIndex(pageIndex + 1);
                            }}
                            pageSize={pageSize}
                            setPageSize={(pageSize) => {
                                setPageSize(pageSize);
                            }}
                        />
                    </div>
                </>
            )}

            {/* ✅ Edit Modal */}
            <Modal
                title={<span className="text-lg font-semibold">{t("Edit Product Group")}</span>}
                open={isModalVisible}
                onOk={handleSave}
                onCancel={handleCancel}
                okText={t("Save")}
                cancelText={t("Cancel")}
                centered
                className="edit-group-modal"
                okButtonProps={{
                    className:
                        "font-semibold bg-primaryBlue hover:bg-primaryBlue text-white text-sm px-6 py-5 rounded-md",
                    // style: { backgroundColor: "primaryBlue", borderColor: "primaryBlue" }
                }}
                cancelButtonProps={{
                    className: "font-semibold text-sm px-6 py-5 rounded-md",
                }}
            >
                <Form layout="vertical" form={form} requiredMark={false}>
                    <Form.Item
                        name="name"
                        label={<span className="text-sm mt-5 font-medium">{t("Group Name (English)")}</span>}
                        rules={[{ required: true, message: t("Please enter English name") }]}
                    >
                        <Input
                            placeholder={t("Enter English name")}
                            className="h-8 text-sm"
                            maxLength={20}
                        />
                    </Form.Item>

                    <Form.Item
                        name="name_ar"
                        label={<span className="text-sm font-medium">{t("Group Name (Arabic)")}</span>}
                        rules={[{ required: true, message: t("Please enter Arabic name") }]}
                    >
                        <Input
                            dir="rtl"
                            placeholder={t("اكتب الاسم هنا")}
                            className="h-8 text-sm"
                            maxLength={20}

                        />
                    </Form.Item>
                </Form>
            </Modal>


        </div>
    );
};

export default ProductGroupTable;













// import React, { useEffect, useMemo, useState, useCallback } from "react";
// import { useTable, useSortBy, usePagination, useRowSelect } from "react-table";
// import "tailwindcss/tailwind.css";
// import { Empty, Input } from "antd";
// import "../../styles.css";
// import Vectorsorting from "../../assets/Vectorsorting.svg";
// import ShimmerLoader from "../../loading";
// import { useTranslation } from "react-i18next";
// import { useNavigate } from "react-router-dom";
// import CustomPagination from "../../components/CustomWidgets/CustomPagination";
// import arrowright from "../../assets/arrowright.svg";
// import { useSelector } from "react-redux";
// import Edit from "../../assets/Edit.svg";
// import deleteicon from "../../assets/Order/Delete.svg";

// const ProductGroupTable = ({ additionalData, pagination, onRowClick, onDelete, onEditLine }) => {
//   const { t, i18n } = useTranslation();
//   const [flag, setFlag] = useState(false);
//   const navigate = useNavigate();
//   const allStores = useSelector(
//     (state) => state?.storeReducer?.selectedStore?.is_select_all_store
//   );

//   const [editingRowId, setEditingRowId] = useState(null);
//   const [editValues, setEditValues] = useState({});

//   const handleEditClick = useCallback((row) => {
//     const id = row.id ?? row?.original?.id;
//     setEditValues({
//       [id]: {
//         name: row.name ?? row.original?.name ?? "",
//         name_ar: row.name_ar ?? row.original?.name_ar ?? "",
//       },
//     });
//     setEditingRowId(id);
//   }, []);

//   const handleCancelClick = useCallback(() => {
//     setEditingRowId(null);
//     setEditValues({});
//   }, []);

//   const handleSaveClick = useCallback(
//     (row) => {
//       const id = row.id ?? row?.original?.id;
//       const currentEdits = editValues[id] ?? {};
//       const updated = {
//         ...row,
//         ...currentEdits,
//       };
//       onEditLine?.(updated);
//       setEditingRowId(null);
//       setEditValues({});
//     },
//     [editValues, onEditLine]
//   );

//   const data = useMemo(() => additionalData || [], [additionalData]);

//   useEffect(() => {
//     if (additionalData?.length === 0 && !flag) {
//       const timer = setTimeout(() => {
//         setFlag(true);
//       }, 3000);
//       return () => clearTimeout(timer);
//     }
//   }, [additionalData, flag]);

//   // ✅ FIXED INPUT STATE HANDLING
//   const handleInputChange = useCallback((rowId, field, value, row) => {
//     setEditValues((prev) => ({
//       ...prev,
//       [rowId]: {
//         ...(prev[rowId] || {
//           name: row.original?.name ?? "",
//           name_ar: row.original?.name_ar ?? "",
//         }),
//         [field]: value,
//       },
//     }));
//   }, []);

//   const columns = useMemo(() => {
//     return [
//       {
//         Header: t("Group Name (English)"),
//         accessor: "name",
//         width: "16%",
//         Cell: ({ row }) => {
//           const rowId = row.original?.id ?? row.id;
//           const isEditing = editingRowId === rowId;
//           const value = editValues[rowId]?.name ?? row.original?.name ?? "";

//           if (!isEditing)
//             return <div className="text-start">{row.original?.name}</div>;

//           return (
//             <Input
//               className="border rounded-lg p-1 w-full"
//               value={value}
//               onChange={(e) =>
//                 handleInputChange(rowId, "name", e.target.value, row)
//               }
//               onClick={(e) => e.stopPropagation()}
//             />
//           );
//         },
//       },
//       {
//         Header: t("Group Name (Arabic)"),
//         accessor: "name_ar",
//         width: "16%",
//         Cell: ({ row }) => {
//           const rowId = row.original?.id ?? row.id;
//           const isEditing = editingRowId === rowId;
//           const value = editValues[rowId]?.name_ar ?? row.original?.name_ar ?? "";

//           if (!isEditing)
//             return <div className="text-start">{row.original?.name_ar}</div>;

//           return (
//             <Input
//               className="border rounded-lg p-1 w-full text-right"
//               dir="rtl"
//               value={value}
//               onChange={(e) =>
//                 handleInputChange(rowId, "name_ar", e.target.value, row)
//               }
//               onClick={(e) => e.stopPropagation()}
//             />
//           );
//         },
//       },
//       {
//         Header: (
//           <div className="text-start" title={t("No. of Products")}>
//             {t("No. of Products")}
//           </div>
//         ),
//         width: "6%",
//         accessor: "no_of_products",
//         Cell: ({ row }) => (
//           <div className="text-start">{t(row.original?.no_of_products)}</div>
//         ),
//       },
//       {
//         Header: t("Actions"),
//         accessor: "actions",
//         width: "15%",
//         Cell: ({ row }) => {
//           const rowId = row.original?.id ?? row.id;
//           const isEditing = editingRowId === rowId;

//           if (!isEditing) {
//             return (
//               <div className="flex gap-2 justify-start">
//                 <img
//                   src={Edit}
//                   className="cursor-pointer h-4 w-4"
//                   alt="Edit"
//                   onClick={(e) => {
//                     e.stopPropagation();
//                     handleEditClick(row.original);
//                   }}
//                 />
//                 <img
//                   src={deleteicon}
//                   className="cursor-pointer h-4 w-4 mx-2"
//                   alt="Delete"
//                   onClick={(e) => {
//                     e.stopPropagation();
//                     onDelete(row.original);
//                   }}
//                 />
//               </div>
//             );
//           }

//           return (
//             <div className="flex gap-2 justify-start">
//               <button
//                 className="px-3 py-1 bg-green-500 text-white rounded-lg hover:bg-green-600"
//                 onClick={(e) => {
//                   e.stopPropagation();
//                   handleSaveClick(row.original);
//                 }}
//               >
//                 {t("Save")}
//               </button>

//               <button
//                 className="px-3 py-1 bg-gray-400 text-white rounded-lg hover:bg-gray-500"
//                 onClick={(e) => {
//                   e.stopPropagation();
//                   handleCancelClick();
//                 }}
//               >
//                 {t("Cancel")}
//               </button>
//             </div>
//           );
//         },
//       },
//       {
//         id: "action",
//         Header: "",
//         headerClassName: "text-start",
//         width: "4%",
//         accessor: (row) => `${row.original},`,
//         Cell: ({ row }) => (
//           <button className=" text-[#1b4497] rounded-lg">
//             <img
//               src={arrowright}
//               alt="arrowright"
//               className={`${i18n.language === "en" ? "" : " rotate-180"}`}
//               height={3}
//               width={20}
//             />
//           </button>
//         ),
//       },
//     ];
//   }, [
//     t,
//     i18n.language,
//     editingRowId,
//     editValues,
//     handleEditClick,
//     handleSaveClick,
//     handleCancelClick,
//     onDelete,
//     handleInputChange,
//   ]);

//   const {
//     headerGroups,
//     prepareRow,
//     page,
//     nextPage,
//     setPageSize,
//     state: { pageSize },
//   } = useTable(
//     {
//       columns,
//       data,
//       initialState: { pageSize: 10 },
//     },
//     useSortBy,
//     usePagination,
//     useRowSelect
//   );

//   const [pageIndex, setPageIndex] = useState(1);

//   useEffect(() => {
//     const pageDetails = {
//       limit: pageSize,
//       offset:
//         pageSize * (pageIndex - 1) < 0 ? 0 : pageSize * (pageIndex - 1),
//     };
//     if (pageSize) pagination(pageDetails);
//   }, [pageSize, pageIndex]);

//   return (
//     <div
//       className="container flex flex-col mx-auto mt-4"
//       style={{ maxWidth: "100%" }}
//     >
//       {additionalData?.length === 0 && !flag ? (
//         <div className="h-screen w-[90%] mt-2 ">
//           <ShimmerLoader />
//         </div>
//       ) : (
//         <>
//           {/* Header Table */}
//           <div>
//             <table
//               className="font-normal max-w-[100%] sm:w-[97.6%] md:w-[97.6%] lg:w-[97.6%] xl:w-[97.6%] overflow-hidden rounded-lg"
//               style={{ tableLayout: "fixed" }}
//             >
//               <thead>
//                 {headerGroups.map((headerGroup) => (
//                   <tr
//                     key={headerGroup.id}
//                     {...headerGroup.getHeaderGroupProps()}
//                     className="bg-[#78CFEC66] text-sm font-semibold overflow-hidden whitespace-nowrap"
//                   >
//                     {headerGroup.headers.map((column) => (
//                       <th
//                         key={column.id}
//                         {...column.getHeaderProps(column.getSortByToggleProps())}
//                         className="py-3 whitespace-nowrap"
//                         style={{ width: column.width }}
//                       >
//                         <div className="flex items-start ms-2 text-sm ">
//                           {column.render("Header")}
//                           {column.isSorted ? (
//                             column.isSortedDesc ? (
//                               <img
//                                 src={Vectorsorting}
//                                 style={{ minHeight: 10, minWidth: 10 }}
//                                 alt="vector"
//                               />
//                             ) : (
//                               <img
//                                 className="rotate-180"
//                                 src={Vectorsorting}
//                                 alt="vector"
//                               />
//                             )
//                           ) : (
//                             ""
//                           )}
//                         </div>
//                       </th>
//                     ))}
//                   </tr>
//                 ))}
//               </thead>
//             </table>
//           </div>

//           {/* Body Table */}
//           <div
//             className="scrollbar-none bg-white"
//             style={{ overflowY: "auto", maxHeight: "600px" }}
//           >
//             <table
//               className="font-normal max-w-[100%] sm:w-[97.6%] md:w-[97.6%] lg:w-[97.6%] xl:w-[97.6%] overflow-hidden rounded-lg"
//               style={{ tableLayout: "fixed" }}
//             >
//               <tbody className="bg-[#F7F9FD] text-center overflow-hidden">
//                 {page.map((row) => {
//                   prepareRow(row);
//                   return (
//                     <tr
//                       key={row.id}
//                       {...row.getRowProps()}
//                       className="relative hover:bg-white cursor-pointer transition duration-300 border-b border-gray-300 fixed-row-height"
//                       onClick={() =>
//                         onRowClick && onRowClick(row.original)
//                       }
//                     >
//                       {row.cells.map((cell, index) => (
//                         <td
//                           key={index}
//                           {...cell.getCellProps()}
//                           className={`p-2 text-[#272728] h-1212 text-sm font-normal ${
//                             index < row.cells.length - 1 ? "relative" : ""
//                           }`}
//                           style={{ width: cell.column.width }}
//                         >
//                           {cell.render("Cell")}
//                         </td>
//                       ))}
//                     </tr>
//                   );
//                 })}
//                 {page.length === 0 && (
//                   <tr>
//                     <td colSpan={"100%"}>
//                       <Empty
//                         className="mt-10"
//                         description={t("No data available")}
//                       />
//                     </td>
//                   </tr>
//                 )}
//               </tbody>
//             </table>
//           </div>

//           {/* Pagination */}
//           <div>
//             <CustomPagination
//               data={data?.length}
//               pageIndex={pageIndex}
//               pageCount={page?.length}
//               canPreviousPage={pageIndex !== 1}
//               canNextPage={data?.length === pageSize}
//               previousPage={() => setPageIndex(pageIndex - 1)}
//               nextPage={() => setPageIndex(pageIndex + 1)}
//               pageSize={pageSize}
//               setPageSize={(size) => setPageSize(size)}
//             />
//           </div>
//         </>
//       )}
//     </div>
//   );
// };

// export default ProductGroupTable;
